import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useApp } from "@/contexts/AppContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Building2 } from "lucide-react";

const REGIMENES = ["GENERAL", "MYPE", "ESPECIAL", "NUEVO_RUS"];

export default function Empresas() {
  const { empresas, reloadEmpresas, switchEmpresa, formatApiError } = useApp();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const blank = { ruc: "", razon_social: "", nombre_comercial: "", direccion: "", telefono: "", email: "", moneda: "PEN", regimen: "GENERAL" };
  const [form, setForm] = useState(blank);

  useEffect(() => { reloadEmpresas(); /* eslint-disable-next-line */ }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing) await api.put(`/empresas/${editing.id}`, form);
      else await api.post("/empresas", form);
      toast.success(editing ? "Empresa actualizada" : "Empresa creada");
      const list = await reloadEmpresas();
      if (!editing && list[list.length - 1]) switchEmpresa(list[list.length - 1]);
      setOpen(false); setEditing(null); setForm(blank);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const editar = (e) => { setEditing(e); setForm(e); setOpen(true); };
  const eliminar = async (e) => {
    if (!confirm(`¿Eliminar ${e.razon_social}?`)) return;
    await api.delete(`/empresas/${e.id}`);
    toast.success("Eliminada");
    reloadEmpresas();
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">Empresas</h1>
          <p className="text-[13px] text-slate-500">Administra tus empresas (Multi-RUC). Cada empresa tiene su propia contabilidad.</p>
        </div>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditing(null); setForm(blank); } }}>
          <DialogTrigger asChild>
            <Button data-testid="btn-nueva-empresa" className="h-9 bg-slate-900 hover:bg-slate-800 text-white">
              <Plus className="w-4 h-4 mr-1" /> Nueva empresa
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{editing ? "Editar empresa" : "Nueva empresa"}</DialogTitle>
            </DialogHeader>
            <form onSubmit={submit} className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <Label className="text-[12px]">RUC</Label>
                <Input data-testid="emp-ruc" value={form.ruc} onChange={(e) => setForm({ ...form, ruc: e.target.value })} maxLength={11} required className="h-9 mono" />
              </div>
              <div>
                <Label className="text-[12px]">Régimen</Label>
                <Select value={form.regimen} onValueChange={(v) => setForm({ ...form, regimen: v })}>
                  <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {REGIMENES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label className="text-[12px]">Razón social</Label>
                <Input data-testid="emp-razon" value={form.razon_social} onChange={(e) => setForm({ ...form, razon_social: e.target.value })} required className="h-9" />
              </div>
              <div className="col-span-2">
                <Label className="text-[12px]">Nombre comercial</Label>
                <Input value={form.nombre_comercial} onChange={(e) => setForm({ ...form, nombre_comercial: e.target.value })} className="h-9" />
              </div>
              <div className="col-span-2">
                <Label className="text-[12px]">Dirección fiscal</Label>
                <Input value={form.direccion} onChange={(e) => setForm({ ...form, direccion: e.target.value })} className="h-9" />
              </div>
              <div>
                <Label className="text-[12px]">Teléfono</Label>
                <Input value={form.telefono} onChange={(e) => setForm({ ...form, telefono: e.target.value })} className="h-9" />
              </div>
              <div>
                <Label className="text-[12px]">Email</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="h-9" />
              </div>
              <DialogFooter className="col-span-2 mt-2">
                <Button data-testid="emp-submit" type="submit" className="bg-slate-900 hover:bg-slate-800 text-white">Guardar</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <table className="tbl w-full">
          <thead>
            <tr>
              <th>RUC</th><th>Razón Social</th><th>Régimen</th><th>Dirección</th><th>Moneda</th><th></th>
            </tr>
          </thead>
          <tbody>
            {empresas.length === 0 ? (
              <tr><td colSpan={6} className="text-center py-8 text-slate-500"><Building2 className="w-8 h-8 mx-auto mb-2 text-slate-300" />No hay empresas aún. Crea la primera para empezar.</td></tr>
            ) : empresas.map((e) => (
              <tr key={e.id} data-testid={`emp-row-${e.ruc}`}>
                <td className="mono font-medium">{e.ruc}</td>
                <td className="font-medium text-slate-800">{e.razon_social}</td>
                <td><span className="badge badge-muted">{e.regimen}</span></td>
                <td className="text-slate-600">{e.direccion}</td>
                <td className="mono">{e.moneda}</td>
                <td className="text-right">
                  <button onClick={() => editar(e)} className="text-slate-600 hover:text-blue-600 mr-2"><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => eliminar(e)} className="text-slate-600 hover:text-rose-600"><Trash2 className="w-4 h-4" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
